import { useRef, useEffect } from 'react'

interface AudioOrbProps {
  isListening: boolean
  audioLevel: number
}

interface Particle {
  x: number; y: number; vx: number; vy: number
  life: number; decay: number; size: number; hue: number
}

export function AudioOrb({ isListening, audioLevel }: AudioOrbProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number>(0)
  const timeRef = useRef(0)
  const smoothLevelRef = useRef(0)
  const particlesRef = useRef<Particle[]>([])
  const isListeningRef = useRef(isListening)
  const audioLevelRef = useRef(audioLevel)

  isListeningRef.current = isListening
  audioLevelRef.current = audioLevel

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const dpr = window.devicePixelRatio || 1
    const size = 220
    canvas.width = size * dpr
    canvas.height = size * dpr
    ctx.scale(dpr, dpr)
    canvas.style.width = `${size}px`
    canvas.style.height = `${size}px`

    const cx = size / 2
    const cy = size / 2
    const baseRadius = 70

    const spawnParticle = (level: number): Particle => {
      const angle = Math.random() * Math.PI * 2
      const speed = 0.5 + Math.random() * 2.5 * level
      const r = baseRadius + 10 + Math.random() * 20
      return {
        x: cx + Math.cos(angle) * r,
        y: cy + Math.sin(angle) * r,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        decay: 0.012 + Math.random() * 0.025,
        size: 1.5 + Math.random() * 3.5 * level,
        hue: Math.random() > 0.5 ? 340 : 25,
      }
    }

    const draw = () => {
      timeRef.current += 0.012
      const t = timeRef.current

      const targetLevel = isListeningRef.current ? audioLevelRef.current : 0
      smoothLevelRef.current += (targetLevel - smoothLevelRef.current) * 0.2
      const level = smoothLevelRef.current

      ctx.clearRect(0, 0, size, size)

      // Outer pulsing rings
      for (let i = 0; i < 3; i++) {
        const phase = (t * 1.5 + i * 0.8) % (Math.PI * 2)
        const expand = Math.sin(phase) * 0.5 + 0.5
        const ringRadius = baseRadius + 12 + i * 14 + expand * (10 + level * 25)
        const alpha = (1 - expand) * (0.12 + level * 0.35)

        ctx.beginPath()
        ctx.arc(cx, cy, ringRadius, 0, Math.PI * 2)
        const hue = 340 - level * 60
        ctx.strokeStyle = `hsla(${hue}, 90%, 70%, ${alpha})`
        ctx.lineWidth = 1.5 + level * 2
        ctx.stroke()
      }

      // Blob layers
      const layers = 5
      for (let layer = layers - 1; layer >= 0; layer--) {
        const layerFactor = layer / layers
        const opacity = 0.18 + (1 - layerFactor) * 0.65

        ctx.beginPath()
        const points = 180
        for (let i = 0; i <= points; i++) {
          const angle = (i / points) * Math.PI * 2
          const wave1 = Math.sin(angle * 3 + t * 2.5 + layer) * (4 + level * 30)
          const wave2 = Math.sin(angle * 5 - t * 2 + layer * 0.7) * (3 + level * 20)
          const wave3 = Math.sin(angle * 8 + t * 4 + layer * 1.5) * (2 + level * 12)
          const wave4 = Math.cos(angle * 2 - t * 1.2) * (3 + level * 8)
          const breath = Math.sin(t * 0.6 + layer * 0.3) * 3

          const r = baseRadius - layerFactor * 10 + wave1 + wave2 + wave3 + wave4 + breath
          const x = cx + Math.cos(angle) * r
          const y = cy + Math.sin(angle) * r
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
        }
        ctx.closePath()

        const hueBase = 340 - level * 80
        const hueShift = layer * 15
        const saturation = 70 + level * 25
        const lightness = 55 + level * 20

        const grad = ctx.createRadialGradient(
          cx - 15 + Math.sin(t * 0.8) * 10, cy - 15 + Math.cos(t * 0.6) * 10, 5,
          cx, cy, baseRadius + 30
        )
        grad.addColorStop(0, `hsla(${hueBase + hueShift + 30}, ${saturation}%, ${lightness + 10}%, ${opacity})`)
        grad.addColorStop(0.5, `hsla(${hueBase + hueShift}, ${saturation}%, ${lightness}%, ${opacity * 0.85})`)
        grad.addColorStop(1, `hsla(${hueBase + hueShift - 30}, ${saturation - 10}%, ${lightness - 10}%, ${opacity * 0.5})`)

        ctx.fillStyle = grad
        ctx.fill()
      }

      // Glow
      if (level > 0.02) {
        const glowRadius = baseRadius + 40 + level * 35
        const glow = ctx.createRadialGradient(cx, cy, baseRadius * 0.4, cx, cy, glowRadius)
        const hueGlow = 340 - level * 80
        glow.addColorStop(0, `hsla(${hueGlow}, 90%, 70%, ${level * 0.35})`)
        glow.addColorStop(0.5, `hsla(${hueGlow - 20}, 80%, 60%, ${level * 0.15})`)
        glow.addColorStop(1, 'rgba(0,0,0,0)')

        ctx.beginPath()
        ctx.arc(cx, cy, glowRadius, 0, Math.PI * 2)
        ctx.fillStyle = glow
        ctx.fill()
      }

      // Particles
      if (isListeningRef.current && level > 0.08) {
        const spawnCount = Math.floor(level * 4)
        for (let s = 0; s < spawnCount; s++) {
          if (particlesRef.current.length < 120) {
            particlesRef.current.push(spawnParticle(level))
          }
        }
      }

      particlesRef.current = particlesRef.current.filter(p => p.life > 0)
      for (const p of particlesRef.current) {
        p.x += p.vx
        p.y += p.vy
        p.vx *= 0.97
        p.vy *= 0.97
        p.life -= p.decay

        const alpha = Math.max(0, p.life)
        const hueShift = 340 - level * 80
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.size * alpha, 0, Math.PI * 2)
        ctx.fillStyle = `hsla(${p.hue === 340 ? hueShift : 25}, 90%, 75%, ${alpha * 0.9})`
        ctx.fill()
      }

      animationRef.current = requestAnimationFrame(draw)
    }

    draw()

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [])

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{
        position: 'absolute', width: 260, height: 260, borderRadius: '50%',
        filter: 'blur(60px)', transition: 'all 0.7s',
        background: `radial-gradient(circle, hsla(${340 - audioLevel * 80}, 80%, 65%, ${0.15 + audioLevel * 0.3}) 0%, hsla(25, 80%, 65%, 0.1) 50%, transparent 70%)`,
        opacity: isListening ? 1 : 0.35,
      }} />
      <canvas ref={canvasRef} style={{ position: 'relative', zIndex: 10 }} />
    </div>
  )
}
